"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Form, Link, Text, TextField } from "ui-components";

import { CookieKeys } from "@/enums/CookieKeys.enum";
import { useCookies } from "@/hooks/useCookies";
import useGetUserCountry from "@/hooks/useGetUserCountry";
import {
  emailCollectionSchema,
  EmailCollectionSchemaType,
} from "@/src/app/[locale]/(public)/jobs/[id]/schema/email-collection";
import {
  LoginResponse,
  NonMemberEntity,
  useAppCreateEmailCollection,
  useAppGetGeneralSettings,
} from "@/src/hooks/api/generated";
import { useAuth } from "@/src/hooks/useAuth";
import { brevoInlineEmailCollection } from "@/utils/brevoClient";

export default function InlineEmailCapture(): JSX.Element | null {
  const { authData } = useAuth();
  const { getCookie, setCookie } = useCookies();
  const { country: userCountry, location: userLocation } = useGetUserCountry();
  const [shouldShow, setShouldShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { data: emailCaptureSettings, status: emailCaptureSettingsStatus } =
    useAppGetGeneralSettings("job_details_inline_email_capture");

  const { mutate: createEmailCollection, status: mutationStatus } =
    useAppCreateEmailCollection();

  const {
    register,
    handleSubmit,
    getFieldState,
    setError,
    formState: { errors },
  } = useForm<EmailCollectionSchemaType>({
    resolver: zodResolver(emailCollectionSchema),
  });

  useEffect(() => {
    if (authData) {
      return;
    }
    if (
      getCookie(CookieKeys.InlineEmailCollectSubmitted) ||
      getCookie(CookieKeys.EmailCollect)
    ) {
      return;
    }
    if (emailCaptureSettingsStatus !== "success" || !emailCaptureSettings) {
      return;
    }

    if (emailCaptureSettings.event_value !== "true") {
      return;
    }

    let sourcesConfig: Array<{
      source: string;
      enabled: boolean;
      intensity?: number;
    }> = [];

    if (Array.isArray(emailCaptureSettings.json)) {
      sourcesConfig = emailCaptureSettings.json as Array<{
        source: string;
        enabled: boolean;
        intensity?: number;
      }>;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const currentSourceRaw = searchParams.get("utm_source");
    const currentSource = currentSourceRaw
      ? currentSourceRaw.toLowerCase()
      : "organic (none)";

    const sourceRule = sourcesConfig.find(
      (s) => s.source.toLowerCase() === currentSource
    );

    if (sourceRule) {
      if (!sourceRule.enabled) {
        return;
      }
    }

    setShouldShow(true);
  }, [authData, emailCaptureSettings, emailCaptureSettingsStatus, getCookie]);

  const onSubmit = (data: EmailCollectionSchemaType): void => {
    const email = data.email?.trim();

    if (!email) {
      setError("email", {
        type: "manual",
        message: "Email is required.",
      });
      return;
    }

    createEmailCollection(
      {
        data: {
          email,
          type: "job-details-inline",
        },
      },
      {
        onSuccess: (successData: NonMemberEntity) => {
          handleSuccess(email, successData);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          let errorMessage = "";
          if (error && typeof error === "object" && error.message) {
            errorMessage = error.message;
          }
          let existingUser;
          if (
            errorMessage ===
            "Email already associated with an account. Please log in to continue."
          ) {
            existingUser =
              error.user || error.response?.data?.user || error.response?.user;
          }
          handleSuccess(email, undefined, existingUser as LoginResponse);
        },
      }
    );
  };

  const handleSuccess = (
    email: string,
    successData?: NonMemberEntity,
    existingUser?: LoginResponse
  ): void => {
    setSubmitted(true);
    setCookie({
      key: CookieKeys.InlineEmailCollectSubmitted,
      value: "true",
      maxAge: 365 * 24 * 60 * 60, // 1 year
    });
    setCookie({
      key: CookieKeys.NonMemberUUID,
      value: successData?.uuid || "",
      maxAge: 2 * 30 * 24 * 60 * 60, // 2 months in seconds
    });
    if (email) {
      const isAuthenticatedContext = !!existingUser;
      const uuid = successData?.uuid || existingUser?.uuid || "";
      const userData = successData || existingUser;

      brevoInlineEmailCollection(
        email,
        isAuthenticatedContext,
        uuid,
        userData,
        userLocation || "",
        userCountry || ""
      );
    }

    setTimeout(() => {
      setShouldShow(false);
    }, 5000);
  };

  if (!shouldShow) {
    return null;
  }

  if (submitted) {
    return (
      <div className="bg-[#fef8e6] p-4 rounded-md mb-6 shadow-sm flex items-center justify-center min-h-[120px]">
        <Text className="text-lg font-medium text-center">
          Thank you! We&apos;ll send similar jobs straight to your inbox.
        </Text>
      </div>
    );
  }

  return (
    <div className="bg-[#fef8e6] p-5 rounded-md mb-6 shadow-sm border border-[#faecd3]">
      <Text className="text-lg font-medium mb-3 text-black">
        Don’t miss out on similar jobs
      </Text>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-3 w-full"
      >
        <div className="flex w-full items-start gap-2">
          <div className="flex-1">
            <TextField
              placeholder="Enter your email address"
              register={register("email")}
              errorMessage={errors.email?.message}
              hasValue={getFieldState("email").isDirty}
            />
          </div>
          <Button
            type="submit"
            size="sm"
            isDisabled={mutationStatus === "pending"}
            isLoading={mutationStatus === "pending"}
            className="!w-16 h-10 !mt-0 !bg-[#205175] hover:!bg-[#1a405d] !text-white !rounded-full"
          >
            Go
          </Button>
        </div>
      </Form>
      <Text className="text-xs text-gray-500 mt-3 leading-tight">
        By signing up you acknowledge you have read our{" "}
        <Link href="/privacy-policy" target="_blank" className="underline">
          Privacy Policy
        </Link>{" "}
        and{" "}
        <Link href="/terms-of-use" target="_blank" className="underline">
          Terms
        </Link>
        .
      </Text>
    </div>
  );
}
